import { isEncompass } from '$Utilities/integrationPlatform';
import { toDate, normalizeToStartOfDay } from '$Utilities/dateUtility';
import { Box, Popover, PopoverOrigin } from '@mui/material';
import { React, moment } from 'Imports';
import { useState, useRef, useEffect } from 'react';
import DateRangeInput from './DateRangeInput';
import DatePickerCalendar from './DatePickerCalendar';
import DateRangeConfirmationButtons from './DateRangeConfirmationButtons';
import classNames from 'classnames';

const styles = require('./DateRangePicker.scss') as {
    inputContainer: string;
    inputContainerMobile: string;
    toText: string;
    toTextEncompass: string;
    toTextGeotab: string;
};

interface IDateRangePickerProps {
    startDate?: Date | moment.Moment;
    endDate?: Date | moment.Moment;
    onDateRangeApply: (startDate: Date, endDate: Date) => void;
    onPickerStateChange?: (isOpen: boolean, hasPendingChanges: boolean) => void;
    isMobileView: boolean;
    maxDays?: number;
}

export const DateRangePicker: React.FC<IDateRangePickerProps> = ({
    startDate,
    endDate,
    onDateRangeApply,
    onPickerStateChange,
    isMobileView = false,
    maxDays,
}) => {
    const inputRef = useRef<HTMLDivElement>(null);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [wiggle, setWiggle] = useState(false);

    // Create initial date range that updates when props change
    const initialDateRange = {
        startDate: normalizeToStartOfDay(toDate(startDate || new Date())),
        endDate: normalizeToStartOfDay(toDate(endDate || new Date())),
    };

    const [dateRange, setDateRange] = useState(initialDateRange);

    // Update dateRange when props change (when parent applies changes)
    useEffect(() => {
        const newInitialDateRange = {
            startDate: normalizeToStartOfDay(toDate(startDate || new Date())),
            endDate: normalizeToStartOfDay(toDate(endDate || new Date())),
        };
        setDateRange(newInitialDateRange);
    }, [startDate, endDate]);

    const hasPendingChanges =
        dateRange.startDate.getTime() !== initialDateRange.startDate.getTime() ||
        dateRange.endDate.getTime() !== initialDateRange.endDate.getTime();

    // Notify parent of picker state changes (if callback provided)
    useEffect(() => {
        onPickerStateChange?.(calendarOpen, hasPendingChanges);
    }, [calendarOpen, hasPendingChanges, onPickerStateChange]);

    const handleDateRangeChange = (startDate: Date, endDate: Date) => {
        setDateRange({ startDate: normalizeToStartOfDay(startDate), endDate: normalizeToStartOfDay(endDate) });
    };

    const handleApplyDateRange = () => {
        onDateRangeApply(dateRange.startDate, dateRange.endDate);
        setCalendarOpen(false);
    };

    const handleCalendarClose = () => {
        setCalendarOpen(false);
        setDateRange(initialDateRange);
    };

    const handlePopoverClose = () => {
        // Only allow popover to close if there are no pending changes
        if (!hasPendingChanges) {
            setCalendarOpen(false);
            setDateRange(initialDateRange);
        } else {
            setWiggle(true);
            setTimeout(() => setWiggle(false), 300); // Remove wiggle animation after 0.3 seconds
        }
    };

    const inputContainerClass = classNames(isMobileView ? styles.inputContainerMobile : styles.inputContainer);

    const anchorOrigin = isMobileView ? { vertical: 'bottom', horizontal: 'right' } : { vertical: 'bottom', horizontal: 'left' };
    const transformOrigin = isMobileView ? { vertical: 'top', horizontal: 'right' } : { vertical: 'top', horizontal: 'left' };

    return (
        <Box sx={isMobileView ? { width: '100%' } : {}}>
            <Box className={inputContainerClass} data-testid={'date-range-picker-container'}>
                <Box ref={inputRef} data-testid={'date-range-picker-start-date-container'}>
                    <DateRangeInput
                        dateRange={dateRange}
                        isActive={calendarOpen}
                        onClick={() => setCalendarOpen(!calendarOpen)}
                        isMobileView={isMobileView}
                    />
                </Box>
            </Box>

            {calendarOpen && (
                <Popover
                    open={calendarOpen}
                    anchorEl={inputRef.current}
                    onClose={handlePopoverClose}
                    anchorOrigin={anchorOrigin as PopoverOrigin}
                    transformOrigin={transformOrigin as PopoverOrigin}
                    className={wiggle ? 'vp-wiggle' : ''}
                    slotProps={{
                        paper: {
                            sx: {
                                width: 'auto',
                                minWidth: isEncompass ? '361px' : '350px',
                                maxWidth: isEncompass ? '361px' : '350px',
                            },
                        },
                    }}
                >
                    <DatePickerCalendar dateRange={dateRange} onSelectedDateRangeChange={handleDateRangeChange} maxDays={maxDays} />

                    {/* Confirmation buttons moved here from DatePickerCalendar */}
                    <Box
                        sx={{
                            padding: '16px',
                            boxShadow: isEncompass ? '0px -3px 5px 0px rgba(0, 0, 0, 0.15)' : 'none',
                        }}
                    >
                        <DateRangeConfirmationButtons
                            handleCancel={handleCalendarClose}
                            handleApply={handleApplyDateRange}
                            applyDisabled={!hasPendingChanges}
                        />
                    </Box>
                </Popover>
            )}
        </Box>
    );
};

export default DateRangePicker;
